// El mes entero de un vistazo.
//
// QUÉ CONTESTA ESTO QUE LA LISTA NO PUEDE CONTESTAR
//
// La lista contesta "¿qué me puse?". El calendario contesta tres cosas
// que en una lista son invisibles:
//
//   1. Qué días NO registraste. En una lista, un día faltante no
//      aparece — no hay nada que ver. En una grilla es un hueco, y los
//      huecos se ven solos.
//   2. Rachas. Doce días seguidos de gris y negro es un bloque oscuro
//      en la pantalla. En una lista son doce líneas de texto iguales
//      que nadie compara.
//   3. Ritmo. Registrar todos los lunes y ningún domingo es una columna
//      llena y una vacía.
//
// POR QUÉ COLORES Y NO FOTOS
//
// Un mes de uso diario son ~90 prendas. Mostrarlas en miniatura serían
// 90 imágenes: cinco veces la página de sugerencias, que el dueño
// acababa de limitar a 18 combinaciones justamente por ancho de banda.
// Un calendario que consume cinco veces más que la pantalla principal
// sería un lujo raro para algo que se mira una vez por semana.
//
// Las franjas de color pesan cero —son divs— y para las tres preguntas
// de arriba dicen MÁS que las fotos: el patrón se ve porque no hay
// detalle que distraiga. Las fotos siguen estando, al tocar el día,
// que es donde de verdad se necesitan.

import { useEffect, useMemo, useRef, useState } from "react";
import { nombresDeDia, nombresDeMes, armarMes, mesesConDatos, resumenDelMes } from "./mes";
import { tintesDelOutfit, DESCONOCIDO } from "./tintes";
import { haceCuanto } from "./fechas";
import { fotoChica } from "./foto";
import { t, tCat, tError, tPlural, useIdioma, idiomaActual, numero } from "./i18n";
import { PerfumeDelRegistro } from "./FrascoBoton";

export default function Calendario({
  registros,
  onAmpliar,
  onBorrar,
  onRepetir,
  onEditar,
}) {
  /* Se repinta cuando llega el idioma. Sin esto el componente depende
   * de que su padre se repinte por otra razón — hoy pasa, y por eso el
   * bug del 2026-08-25 se veía en unas pantallas y no en otras. */
  useIdioma();
  /* El idioma entra en las dependencias: sin él, cambiar de idioma
   * dejaría las etiquetas de mes en el idioma anterior hasta que
   * cambiaran los registros. Es el mismo agujero que el useIdioma que
   * faltaba, un piso más abajo. */
  const idioma = idiomaActual();
  const meses = useMemo(() => mesesConDatos(registros, idioma), [registros, idioma]);
  const DIAS = nombresDeDia(idioma);
  const MESES = nombresDeMes(idioma);

  // El mes que se está mirando, identificado por "2026-7" y NO por su
  // posición en la lista.
  //
  // POR QUÉ NO POR POSICIÓN
  //
  // `meses` sale de los registros, así que cambia cuando se registra
  // algo. Con "Repetir" mirando julio, si agosto todavía no tenía nada,
  // agosto se agrega ADELANTE y el índice 0 pasa a señalar otro mes: la
  // pantalla saltaría sola de julio a agosto sin que nadie la mueva.
  //
  // Guardando la clave del mes, la lista puede crecer o encogerse abajo
  // y vos seguís donde estabas. `null` = todavía no elegiste, se abre el
  // más nuevo con datos, que casi siempre es el actual.
  const [claveMes, setClaveMes] = useState(null);
  const [diaAbierto, setDiaAbierto] = useState(null);

  const posicion = Math.max(
    0,
    meses.findIndex((m) => `${m.anio}-${m.mes}` === claveMes)
  );
  const actual = meses[posicion];

  const irA = (i) => {
    const destino = meses[i];
    if (!destino) return;
    setClaveMes(`${destino.anio}-${destino.mes}`);
    setDiaAbierto(null);
  };

  const semanas = useMemo(
    () => (actual ? armarMes(registros, actual.anio, actual.mes) : []),
    [registros, actual]
  );
  const resumen = useMemo(() => resumenDelMes(semanas), [semanas]);

  if (!actual) {
    return (
      <p className="tenue">
        {t("calendario.vacio", "Todavía no hay nada que mostrar en el calendario. Aparece solo cuando registras outfits.")}
      </p>
    );
  }

  // Los meses vienen del más nuevo al más viejo, así que "anterior en el
  // tiempo" es avanzar en el arreglo. Se navega entre meses CON DATOS y
  // no de a uno: saltar por doce meses vacíos para llegar al año pasado
  // no le sirve a nadie.
  const hayMasViejo = posicion < meses.length - 1;
  const hayMasNuevo = posicion > 0;

  const abierto = diaAbierto
    ? semanas.flat().find((c) => c.clave === diaAbierto)
    : null;

  return (
    <div className="calendario">
      <div className="calendario-cabecera">
        <button
          className="boton-secundario calendario-flecha"
          onClick={() => irA(posicion + 1)}
          disabled={!hayMasViejo}
          aria-label={t("calendario.mesAnterior", "Mes anterior")}
        >
          ‹
        </button>

        <div className="calendario-titulo">
          <strong>
            {MESES[actual.mes]} {actual.anio}
          </strong>
          <span className="texto-mini tenue">
            {tPlural("plural.diasConRegistro", resumen.transcurridos, undefined, {
              registrados: resumen.registrados,
            })}
          </span>
        </div>

        <button
          className="boton-secundario calendario-flecha"
          onClick={() => irA(posicion - 1)}
          disabled={!hayMasNuevo}
          aria-label={t("calendario.mesSiguiente", "Mes siguiente")}
        >
          ›
        </button>
      </div>

      <div className="calendario-dias">
        {DIAS.map((d) => (
          <span key={d} className="texto-mini tenue calendario-dia-nombre">
            {d}
          </span>
        ))}
      </div>

      <div className="calendario-grilla">
        {semanas.flat().map((celda) => (
          <Celda
            key={celda.clave}
            celda={celda}
            abierta={celda.clave === diaAbierto}
            onAbrir={() =>
              setDiaAbierto((v) => (v === celda.clave ? null : celda.clave))
            }
          />
        ))}
      </div>

      {abierto && (
        <DiaAbierto
          celda={abierto}
          onAmpliar={onAmpliar}
          onBorrar={onBorrar}
          onRepetir={onRepetir}
          onEditar={onEditar}
          onCerrar={() => setDiaAbierto(null)}
        />
      )}
    </div>
  );
}

function Celda({ celda, abierta, onAbrir }) {
  const tieneAlgo = celda.outfits.length > 0;

  // Los colores de TODOS los outfits del día. Si te cambiaste para
  // salir, el día tiene los dos — es un día de más ropa y se ve así.
  const tintes = tintesDelOutfit(celda.outfits.flatMap((o) => o.prendas || []));

  const clases = [
    "calendario-celda",
    celda.delMes ? "" : "calendario-celda-fuera",
    celda.esHoy ? "calendario-celda-hoy" : "",
    tieneAlgo ? "calendario-celda-con" : "calendario-celda-sin",
    abierta ? "calendario-celda-abierta" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Los días de relleno no se pueden tocar: pertenecen a otro mes y
  // abrirlos mostraría un outfit que no está en la grilla que mirás.
  const clickable = celda.delMes && tieneAlgo;

  return (
    <button
      className={clases}
      onClick={clickable ? onAbrir : undefined}
      disabled={!clickable}
      title={tieneAlgo ? tintes.map((t) => t.nombre).join(" · ") : undefined}
    >
      <span className="calendario-numero">{numero(celda.dia)}</span>
      {tieneAlgo && (
        <span className="calendario-franjas">
          {tintes.map((t, i) => (
            <span
              key={`${t.nombre}-${i}`}
              className={`calendario-franja ${
                t.tinte === DESCONOCIDO ? "calendario-franja-desconocida" : ""
              }`}
              style={t.tinte === DESCONOCIDO ? undefined : { background: t.tinte }}
              title={t.nombre}
            />
          ))}
        </span>
      )}
    </button>
  );
}

// El día abierto, con las fotos.
//
// Va DEBAJO de la grilla y no en un modal a propósito. Un modal tapa el
// calendario, y el sentido de tocar un día es compararlo con los de al
// lado: "¿esto es lo mismo que me puse el martes?". Tapando el mes esa
// pregunta no se puede contestar.
function DiaAbierto({ celda, onAmpliar, onBorrar, onRepetir, onEditar, onCerrar }) {
  /* ⚠️  `MESES` VIVÍA EN `Calendario` Y ACÁ NO EXISTE (2026-09-05).
   *
   * Era `ReferenceError: MESES is not defined` al abrir un día: la
   * pantalla del calendario se caía entera. Lo encontró el guardián
   * `no-undef` de test_orden_declaraciones.js §3, no una persona. */
  useIdioma();
  const MESES = nombresDeMes(idiomaActual());

  /* ⚠️  EL DETALLE SE LLEVA A LA VISTA AL ABRIR UN DÍA (22/9).
   *
   * Va debajo de la grilla (ver arriba por qué no es un modal), y la
   * grilla son seis semanas: en el celular ocupa la pantalla entera. Se
   * tocaba un día y aparentemente no pasaba nada —el detalle, con
   * «Repetir» y «Editar», quedaba abajo, fuera de la vista—, y el dueño
   * lo reportó como «en el calendario no puedo editar». En la lista el
   * botón está a la vista; acá hay que traerlo.
   *
   * `block: "nearest"`: si el detalle ya se ve entero (pantalla
   * grande), no se mueve nada. Depende de `celda.clave` para que
   * tocar otro día también lo traiga. */
  const caja = useRef(null);
  useEffect(() => {
    caja.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [celda.clave]);

  return (
    <div className="calendario-detalle" ref={caja}>
      <div className="calendario-detalle-cabecera">
        <strong>
          {t("calendario.diaDe", "{dia} de {mes}", {
            dia: celda.dia,
            mes: MESES[new Date(celda.clave + "T12:00:00").getMonth()],
          })}
        </strong>
        <button className="boton-texto" onClick={onCerrar}>
          {t("comun.cerrar", "Cerrar")}
        </button>
      </div>

      {celda.outfits.map((o) => (
        <OutfitDelDia
          key={o.id}
          outfit={o}
          onAmpliar={onAmpliar}
          onBorrar={onBorrar}
          onRepetir={onRepetir}
          onEditar={onEditar}
        />
      ))}
    </div>
  );
}

// UN outfit dentro del día abierto.
//
// Es un componente aparte y no JSX suelto porque tiene estado propio:
// el "Repetir" pasa por tres momentos —listo, guardando, hecho— y ese
// estado es de ESTE outfit, no del día. Con dos outfits el mismo día y
// un estado compartido, repetir uno marcaría los dos como registrados.
function OutfitDelDia({ outfit: o, onAmpliar, onBorrar, onRepetir, onEditar }) {
  /* ⚠️  `idioma` VIVÍA EN `Calendario` Y ACÁ NO EXISTE (2026-09-05).
   *
   * Mismo caso que `MESES` en `DiaAbierto`: ReferenceError al dibujar
   * un outfit del día. `haceCuanto` recibe el idioma por parámetro a
   * propósito (ver fechas.js), así que hay que dárselo desde acá. */
  useIdioma();
  const idioma = idiomaActual();
  const [repitiendo, setRepitiendo] = useState(null);
  // El mensaje de por qué no se pudo. Vive por outfit, igual que
  // `repitiendo`: con un estado compartido, un error en uno se
  // mostraría en todos.
  const [error, setError] = useState("");

  // Una prenda borrada del armario sigue en el historial —el outfit se
  // usó de verdad ese día— pero no se puede volver a registrar ni a
  // editar. Repetir con el id de algo que ya no existe sería un error
  // del servidor con un mensaje que no ayuda.
  const vivas = (o.prendas || []).filter((p) => p.activa !== false);
  const faltan = (o.prendas || []).length - vivas.length;
  const sePuede = vivas.length > 0;

  async function repetir() {
    if (!sePuede) return;
    setRepitiendo("yendo");
    setError("");
    try {
      await onRepetir?.(vivas.map((p) => p.id));
      setRepitiendo("ok");
    } catch (e) {
      /* ⚠️  ANTES ESTO ERA UN CATCH VACÍO (2026-08-24, ME-10).
       *
       * El usuario tocaba "Repetir", veía "...", y el botón volvía a
       * decir "Repetir". Sin cartel, sin nada. No había forma de saber
       * si el outfit se había registrado o no — y registrar dos veces
       * porque no se sabe es peor que no registrar.
       *
       * `Historial.repetir` no atrapa nada, así que el error subía hasta
       * acá y se descartaba. */
      setRepitiendo(null);
      setError(tError(e));
    }
  }

  return (
    <div className="calendario-outfit">
      {error && <p className="mensaje-error texto-mini" role="alert">{error}</p>}
          <div className="calendario-fotos">
            {(o.prendas || []).map((p) => (
              <div key={p.id} className="calendario-foto">
                {fotoChica(p) ? (
                  <img loading="lazy" decoding="async"
                    src={fotoChica(p)}
                    alt={p.nombre}
                    className="prenda-foto"
                    onClick={() => onAmpliar?.(p)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      /* Enter y espacio hacen lo mismo que el click, sea lo que
                         sea. No se duplica la expresión del onClick: se dispara
                         el click de verdad. Copiar el handler exigía parsear JSX
                         con llaves anidadas, que es justamente lo que este
                         proyecto ya aprendió a no hacer. */
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  />
                ) : (
                  <div className="prenda-foto prenda-sin-foto">
                    <span className="tenue texto-mini">
                      {p.activa === false
                        ? t("calendario.borrada", "borrada")
                        : t("calendario.sinFoto", "sin foto")}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

      <p className="texto-mini tenue" style={{ margin: "4px 0 0" }}>
        {(o.prendas || []).map((p) => p.nombre).join(" · ")}
      </p>
        {/* El perfume de ese día, si lo hubo (22/9). */}
        <PerfumeDelRegistro perfume={o.perfume} />
      <p className="texto-mini" style={{ margin: 0 }}>
        {haceCuanto(o.usada_en, { idioma })}
        {o.ocasion && <span className="tenue"> · {tCat("ocasion", o.ocasion)}</span>}
      </p>

      {/* REPETIR y EDITAR.
          Este es el atajo que antes vivía arriba del armador como
          "Repetir uno de estos". Acá funciona mejor porque elegís
          cualquier día de cualquier mes, viéndolo en contexto, en vez
          de los últimos seis a ciegas.

          Son DOS botones y no uno con un menú porque son dos intenciones
          distintas y las dos son de un toque:
            Repetir  = ponérmelo igual hoy. Registra y listo.
            Editar   = este registro está mal cargado. Abre el armador
                       con sus prendas y al guardar CORRIGE este mismo
                       registro (21/9). Antes creaba uno nuevo y dejabas
                       dos.

          Editar se ofrece aunque todas las prendas estén borradas del
          armario: igual se le puede corregir la fecha o la ocasión.

          "Repetir" registra en el DÍA DE HOY, no en el día que estás
          mirando. Es lo único que tiene sentido: el día que mirás ya
          está registrado, volver a registrarlo ahí sería duplicarlo. */}
      <div className="calendario-acciones">
        {sePuede && (
          <button
            className="boton-secundario"
            onClick={repetir}
            disabled={repitiendo === "yendo" || repitiendo === "ok"}
            title={t("calendario.repetirTitulo", "Registrar esta misma combinación con fecha de hoy")}
          >
            {repitiendo === "ok"
              ? t("calendario.registradoHoy", "✓ Registrado hoy")
              : repitiendo === "yendo"
              ? "..."
              : t("calendario.repetir", "Repetir")}
          </button>
        )}

        <button
          className="boton-secundario"
          onClick={() => onEditar?.(o)}
          title={t("calendario.editarTitulo", "Corregir las prendas, la fecha o la ocasión de este registro")}
        >
          {t("calendario.editar", "Editar")}
        </button>
      </div>

      {/* Se dice, en vez de que el botón desaparezca sin explicación. */}
      {faltan > 0 && (
        <p className="texto-mini tenue" style={{ margin: "4px 0 0" }}>
          {tPlural("plural.prendasFaltan", faltan)}
          {sePuede ? t("calendario.noEntran", ", así que no entran al repetirlo.") : "."}
        </p>
      )}

      <button
        className="boton-texto registro-borrar"
        onClick={() => onBorrar?.(o.id)}
      >
        {t("calendario.borrarRegistro", "Borrar registro")}
      </button>
    </div>
  );
}
